import { TranslateService } from '@ngx-translate/core';
import { Component, OnInit, Input, Output, EventEmitter, OnDestroy, ViewChild, ViewChildren, QueryList, ElementRef } from '@angular/core';
import { UserService } from 'src/app/core/service/user.service';
import { User } from 'src/app/user/model/user';
import { Team, TeamEdit, TeamAssignEdit } from '../model/team';
import { NGXLogger } from 'ngx-logger';
import { MessageHandlerService } from 'src/app/core/utility/message-handler.service';
import { TaskService } from 'src/app/core/service/task.service';
import { TeamService } from 'src/app/core/service/team.service';
import { MessengerService, MessageSubject } from 'src/app/core/service/messenger.service';
import { AuthenticationService } from 'src/app/core/authentication/authentication.service';
import { SettingService } from 'src/app/core/service/setting.service';
import { Subscription } from 'rxjs';
import { IntlService } from '@progress/kendo-angular-intl';
import { TooltipDirective } from '@progress/kendo-angular-tooltip';

@Component({
  selector: 'app-team-list',
  templateUrl: './team-list.component.html',
  styleUrls: ['./team-list.component.scss']
})
export class TeamListComponent implements OnInit, OnDestroy
{
  @ViewChild('patientCareTeamCollapsible') public patientCareTeamCollapsible: any;
  @ViewChild('caseCareTeamCollapsible') public caseCareTeamCollapsible: any;
  @ViewChildren(TooltipDirective) public tooltipDir: QueryList<TooltipDirective>;

  @Input() public caseId: number;
  @Input() public memberId: number;
  
  @Output() public assigned = new EventEmitter<boolean>();
  @Output() public addTask = new EventEmitter<number>();
  @Output() public modifyTeam = new EventEmitter<boolean>();

  public patientLabel = {};
  public openTaskAssign = false;
  public userSelected: User;
  public team: any[] = [];
  public teamNonUser: any[] = [];
  public isNewUserInTeam: boolean;
  public editDataItemTeam: TeamEdit;
  public editDataItemTeamNonUser: TeamEdit;
  public editDataItemAssign: TeamAssignEdit;
  public username: string;
  public isNewNonUserInTeam: boolean;
  public confirmAddToCareTeamDialogOpened = false;
  public addingCareTeam: TeamEdit;
  public autoCreatedStaticText: string;
  public cancelledReferralText: string;
  public editDataItemAppointment: any;
  public appointmentsEnabled = false;
  public collapseTeamMember = new Map();
  public patientCareTeamCollapsed = false;

  /**
   * Indicate if Kno2 integration is enabled
   */
  public kno2Enabled = false;

  private messagingSubscription: Subscription;

  constructor(
    private teamService: TeamService,
    private logger: NGXLogger,
    private userService: UserService,
    private taskService: TaskService,
    private messageHandlerService: MessageHandlerService,
    private messengerService: MessengerService,
    private authenticationService: AuthenticationService,
    private settingService: SettingService,
    private translate: TranslateService,
    public intl: IntlService
  )
  {
  }

  public ngOnInit(): void
  {
    this.settingService.getCachedSessionSettings().subscribe({
      next: (sessionSetting) =>
      {
        this.kno2Enabled = sessionSetting.kno2Enabled;
        this.appointmentsEnabled = (sessionSetting.appointmentsRoles !== '') ?
          this.userService.isRoleInList(this.authenticationService.currentUser.role, sessionSetting.appointmentsRoles) : false;
        this.patientLabel = { patientLabel: sessionSetting.patientLabel };
      }
    });

    this.messagingSubscription = this.messengerService.receiveMessage().subscribe({
      next: message =>
      {
        if (message.type === 'addToCareTeam')
        {
          this.addingCareTeam = message.data.teamEdit;
          this.confirmAddToCareTeamDialogOpened = true;
        }
        else
          if ((this.caseId === undefined) &&
            (message.type === 'openCareTeamMemberPatient' || message.type === 'openCareTeamMemberCase') && message.data)
          {
            this.openTeamNonUserEditDialog(message.data.id);
          }
      }
    });

    this.translate.get(['PROVIDER-AUTO-CREATED-HINT', 'CANCELLED-REFERRAL']).subscribe({
      next: (res) =>
      {
        this.autoCreatedStaticText = res['PROVIDER-AUTO-CREATED-HINT'];
        this.cancelledReferralText = res['CANCELLED-REFERRAL'];
      }
    });

    if (this.memberId !== null && this.memberId !== undefined && (this.caseId === null || this.caseId === undefined))
    {
      this.userService.getUsersByMember(this.memberId).subscribe({
        next: data =>
        {
          data.forEach(d =>
          {
            this.userService.getUserRoleAccess(d.userId, "MessagesRoles").subscribe({
              next: (access) =>
              {
                d.messageAccess = access;
                this.team.push(d);
                this.collapseTeamMember.set(d.id, true);
              }
            });
          });
        },
        error: (error) =>
        {
          this.logger.error(error);
        }
      });

      // Retrieve Person and Provider Care Team
      this.userService.getNonUsersByMember(this.memberId, null).subscribe({
        next: (data) =>
        {
          data.forEach(d =>
          {
            d.createdOn = new Date(d.createdOn);
            this.teamNonUser.push(d);
            this.collapseTeamMember.set(d.id, true);
          });
        },
        error: (error) =>
        {
          this.logger.error(error);
        }
      });
    }
    else if (this.caseId !== null && this.caseId !== undefined)
    {
      this.userService.getTeamUsersByCase(this.caseId).subscribe({
        next: (data) =>
        {
          data.forEach(d =>
          {
            this.userService.getUserRoleAccess(d.userId, "MessagesRoles").subscribe({
              next: (access) =>
              {
                d.messageAccess = access;
                this.team.push(d);
                this.collapseTeamMember.set(d.id, true);
              }
            });
          });
        },
        error: (error) =>
        {
          this.logger.error(error);
        }
      });

      // Retrieve Person and Provider Care Team
      this.userService.getTeamNonUsersByCase(this.caseId).subscribe({
        next: (data) =>
        {
          data.forEach(d =>
          {
            d.createdOn = new Date(d.createdOn);
            this.teamNonUser.push(d);
            this.collapseTeamMember.set(d.id, true);
          });
        },
        error: (error) =>
        {
          this.logger.error(error);
        }
      });
    }
  }

  public ngOnDestroy(): void
  {
    if (this.messagingSubscription !== undefined) { this.messagingSubscription.unsubscribe(); }
  }

  /**
   * Get the user and open the dialog of team details
   *
   * @param user User to send at the component
   */
  public openTeamEditDialog(careTeam: Team): void
  {
    this.isNewUserInTeam = false;
    setTimeout(() =>
    {
      this.teamService.getCareTeam(careTeam.id).subscribe({
        next: (aTeam) =>
        {
          const teamEdit = new TeamEdit();

          teamEdit.id = aTeam.id;
          teamEdit.member = aTeam.memberId;
          teamEdit.user = { id: aTeam.userId, name: aTeam.userName };
          teamEdit.role = { id: aTeam.roleId, name: aTeam.roleName, teamType: aTeam.roleTypeId };
          teamEdit.subRole = { id: aTeam.subRoleId, name: aTeam.subRoleName };
          teamEdit.notification = aTeam.notification;

          this.editDataItemTeam = teamEdit;
        }
      });
    });
  }

  /**
   * Get the user and open the dialog of team details
   *
   * @param user User to send at the component
   */
  public openTeamNonUserEditDialog(id: number): void
  {
    this.isNewNonUserInTeam = false;
    setTimeout(() =>
    {
      this.teamService.getCareTeam(id).subscribe({
        next: (aTeam) =>
        {
          const teamEdit = new TeamEdit();

          teamEdit.id = aTeam.id;
          teamEdit.member = aTeam.memberId;
          teamEdit.role = { id: aTeam.roleId, name: aTeam.roleName, teamType: aTeam.roleTypeId };
          teamEdit.subRole = { id: aTeam.subRoleId, name: aTeam.subRoleName };
          teamEdit.firstName = aTeam.firstName;
          teamEdit.lastName = aTeam.lastName;
          teamEdit.middleName = aTeam.middleName;
          teamEdit.suffix = aTeam.suffix;
          teamEdit.professionalSuffix = aTeam.professionalSuffix;
          teamEdit.spokenLanguages = aTeam.spokenLanguages;
          teamEdit.gender = aTeam.gender;
          teamEdit.organization = aTeam.organization;
          teamEdit.url = aTeam.url;
          teamEdit.cellPhoneNumber = aTeam.cellPhoneNumber;
          teamEdit.homePhoneNumber = aTeam.homePhoneNumber;
          teamEdit.homePhoneNumberExtension = aTeam.homePhoneNumberExtension;
          teamEdit.faxNumber = aTeam.faxNumber;
          teamEdit.workPhoneNumber = aTeam.workPhoneNumber;
          teamEdit.workPhoneNumberExtension = aTeam.workPhoneNumberExtension;
          teamEdit.otherPhoneNumber = aTeam.otherPhoneNumber;
          teamEdit.taxNo = aTeam.taxNo;
          teamEdit.title = aTeam.title;
          teamEdit.personId = aTeam.personId;
          teamEdit.email = aTeam.email;
          teamEdit.case = aTeam.caseId;
          teamEdit.accountCreated = aTeam.accountCreated;
          teamEdit.createAccount = aTeam.createAccount;
          teamEdit.accountRole = aTeam.accountRole;
          teamEdit.accountOrganization = aTeam.accountOrganization;
          teamEdit.npi = aTeam.npi;
          teamEdit.directAddress = aTeam.directAddress;
          teamEdit.providerDirectorySource = aTeam.providerDirectorySource;
          teamEdit.streetLine = aTeam.provider ? aTeam.provider.streetLine : aTeam.streetLine;
          teamEdit.streetLineAdditional = aTeam.provider ? aTeam.provider.streetLineAdditional : aTeam.streetLineAdditional;
          teamEdit.city = aTeam.provider ? aTeam.provider.city : aTeam.city;
          teamEdit.county = aTeam.provider ? aTeam.provider.county : aTeam.county;
          teamEdit.postalCode = aTeam.provider ? aTeam.provider.postalCode : aTeam.postalCode;
          teamEdit.state = aTeam.provider ? aTeam.provider.state : aTeam.state;
          teamEdit.country = aTeam.provider ? aTeam.provider.country : aTeam.country;
          teamEdit.notification = aTeam.notification;
          teamEdit.identifiers = aTeam.identifiers;
          teamEdit.extensions = aTeam.extensions;
          teamEdit.description = aTeam.description;
          teamEdit.from = aTeam.from;
          teamEdit.to = aTeam.to;
          teamEdit.extensions = aTeam.extensions;

          this.editDataItemTeamNonUser = teamEdit;
        }
      });
    });
  }

  public addUserIntoTeam(): void
  {
    this.isNewUserInTeam = true;
    setTimeout(() => { this.editDataItemTeam = new TeamEdit(); });
  }

  public addNonUserIntoTeam(): void
  {
    this.isNewNonUserInTeam = true;
    setTimeout(() => { this.editDataItemTeamNonUser = new TeamEdit(); });
  }

  /**
   * Save Team record
   *
   * @param team TeamEdit or TeamProviderEdit type
   */
  public onSaveTeam(team: any): void
  {
    const teamToSave = new Team();
    teamToSave.id = team.id;
    teamToSave.memberId = team.member;
    teamToSave.caseId = team.case;
    teamToSave.roleId = team.role.id;
    teamToSave.npi = team.npi;
    teamToSave.providerDirectorySource = team.providerDirectorySource;
    teamToSave.notification = team.notification;

    if (team.role.teamType !== null)
    {
      if (team.role.teamType.id !== null && team.role.teamType.id !== undefined)
      {
        teamToSave.roleTypeId = team.role.teamType.id;
      }
      else
      {
        teamToSave.roleTypeId = team.role.teamType;
      }

      if (team.subRole !== null)
      {
        teamToSave.subRoleId = team.subRole.id;
        if (team.subRole.displayName !== undefined)
        {
          teamToSave.subRoleName = team.subRole.displayName;
        }
        else
        {
          teamToSave.subRoleName = team.subRole.name;
        }
      }
    }
    else
    {
      teamToSave.subRoleId = null;
      teamToSave.subRoleName = null;
    }

    // Save User if provided
    if (team.user && team.user !== undefined && team.user.id > 0)
    {
      teamToSave.userId = team.user.id;
    }

    this.teamService.createCareTeam(teamToSave).subscribe({
      next: (data) =>
      {
        if (team.id !== undefined)
        {
          this.team = this.team.filter(t => t.id !== data.id);
        }
        this.team.push(data);
        this.team.sort((a, b) => a.userFullName.localeCompare(b.userFullName));
        this.messageHandlerService.displaySuccess(this.isNewUserInTeam ?
          'USER-TEAM-ENROLLED-SUCCESFULLY' : 'USER-TEAM-UPDATED-SUCCESFULLY');
        this.modifyTeam.emit(true);
      },
      error: (error) =>
      {
        this.messageHandlerService.displayError(error);
        this.logger.error(error);
      }
    });
  }

  public onDeleteTeam(team: TeamEdit): void
  {
    this.teamService.deleteCareTeam(team.id).subscribe({
      next: () =>
      {
        this.team = this.team.filter(t => t.id !== team.id);
        this.messageHandlerService.displaySuccess('USER-TEAM-REMOVED-SUCCESFULLY');
        this.modifyTeam.emit(true);
      },
      error: (error) =>
      {
        this.messageHandlerService.displayError(error);
        this.logger.error(error);
      }
    });
  }

  public openTaskAssignDialog(careTeam: Team): void
  {
    this.editDataItemAssign = new TeamAssignEdit();
    this.editDataItemAssign.member = { id: careTeam.memberId };
    this.editDataItemAssign.user = { id: careTeam.userId, username: careTeam.userName };
  }

  public onCancelAssignee(): void
  {
    this.username = null;
  }

  public onSaveAssignee(teamAssignee: TeamAssignEdit): void
  {
    teamAssignee.tasks.forEach(task =>
    {
      this.taskService.assignTask(task.id, teamAssignee.user.username).subscribe({
        next: () =>
        {
          this.messageHandlerService.displaySuccess('TASK-ASSIGNED');
          this.assigned.emit(true);
        },
        error: (error) =>
        {
          this.messageHandlerService.displayError(error);
        }
      });
    });
  }

  public initiateTask(userId: number): void
  {
    this.addTask.emit(userId);
  }

  public onSaveTeamNonUser(teamNonUserEdit: TeamEdit): void
  {
    const teamNonUserToSave = new Team();
    teamNonUserToSave.id = teamNonUserEdit.id;
    teamNonUserToSave.memberId = teamNonUserEdit.member;
    teamNonUserToSave.caseId = teamNonUserEdit.case;
    teamNonUserToSave.roleId = teamNonUserEdit.role.id;
    teamNonUserToSave.roleName = teamNonUserEdit.role.name;
    teamNonUserToSave.npi = teamNonUserEdit.npi;
    teamNonUserToSave.directAddress = teamNonUserEdit.directAddress;
    teamNonUserToSave.providerDirectorySource = teamNonUserEdit.providerDirectorySource;
    teamNonUserToSave.notification = teamNonUserEdit.notification;

    if (teamNonUserEdit.role.teamType !== null)
    {
      if (teamNonUserEdit.role.teamType.id !== null && teamNonUserEdit.role.teamType.id !== undefined)
      {
        teamNonUserToSave.roleTypeId = teamNonUserEdit.role.teamType.id;
      }
      else
      {
        teamNonUserToSave.roleTypeId = teamNonUserEdit.role.teamType;
      }

      if (teamNonUserEdit.subRole !== null)
      {
        teamNonUserToSave.subRoleId = teamNonUserEdit.subRole.id;
        if (teamNonUserEdit.subRole.displayName !== undefined)
        {
          teamNonUserToSave.subRoleName = teamNonUserEdit.subRole.displayName;
        }
        else
        {
          teamNonUserToSave.subRoleName = teamNonUserEdit.subRole.name;
        }
      }
    }
    else
    {
      teamNonUserToSave.subRoleId = null;
      teamNonUserToSave.subRoleName = null;
    }
    teamNonUserToSave.personId = teamNonUserEdit.personId;
    teamNonUserToSave.firstName = teamNonUserEdit.firstName;
    teamNonUserToSave.lastName = teamNonUserEdit.lastName;
    teamNonUserToSave.middleName = teamNonUserEdit.middleName;
    teamNonUserToSave.suffix = teamNonUserEdit.suffix;
    teamNonUserToSave.professionalSuffix = teamNonUserEdit.professionalSuffix;
    teamNonUserToSave.spokenLanguages = teamNonUserEdit.spokenLanguages;
    teamNonUserToSave.gender = teamNonUserEdit.gender;
    teamNonUserToSave.email = teamNonUserEdit.email;
    teamNonUserToSave.homePhoneNumber = teamNonUserEdit.homePhoneNumber;
    teamNonUserToSave.homePhoneNumberExtension = teamNonUserEdit.homePhoneNumberExtension;
    teamNonUserToSave.cellPhoneNumber = teamNonUserEdit.cellPhoneNumber;
    teamNonUserToSave.otherPhoneNumber = teamNonUserEdit.otherPhoneNumber;
    teamNonUserToSave.workPhoneNumber = teamNonUserEdit.workPhoneNumber;
    teamNonUserToSave.workPhoneNumberExtension = teamNonUserEdit.workPhoneNumberExtension;
    teamNonUserToSave.taxNo = teamNonUserEdit.taxNo;
    teamNonUserToSave.faxNumber = teamNonUserEdit.faxNumber;
    teamNonUserToSave.organization = teamNonUserEdit.organization;
    teamNonUserToSave.url = teamNonUserEdit.url;
    teamNonUserToSave.title = teamNonUserEdit.title;
    teamNonUserToSave.createAccount = teamNonUserEdit.createAccount;
    teamNonUserToSave.accountRole = teamNonUserEdit.accountRole;
    teamNonUserToSave.accountOrganization = teamNonUserEdit.accountOrganization;
    teamNonUserToSave.streetLine = teamNonUserEdit.streetLine;
    teamNonUserToSave.streetLineAdditional = teamNonUserEdit.streetLineAdditional;
    teamNonUserToSave.city = teamNonUserEdit.city;
    teamNonUserToSave.county = teamNonUserEdit.county;
    teamNonUserToSave.postalCode = teamNonUserEdit.postalCode;
    teamNonUserToSave.state = teamNonUserEdit.state;
    teamNonUserToSave.country = teamNonUserEdit.country;
    teamNonUserToSave.identifiers = teamNonUserEdit.identifiers;
    teamNonUserToSave.extensions = teamNonUserEdit.extensions;
    teamNonUserToSave.description = teamNonUserEdit.description;
    teamNonUserToSave.from = teamNonUserEdit.from;
    teamNonUserToSave.to = teamNonUserEdit.to;
    teamNonUserToSave.longitude = teamNonUserEdit.longitude;
    teamNonUserToSave.latitude = teamNonUserEdit.latitude;

    this.teamService.createCareTeam(teamNonUserToSave).subscribe({
      next: (data) =>
      {
        teamNonUserToSave.id = data.id;
        if (teamNonUserEdit.id !== undefined)
        {
          this.teamNonUser = this.teamNonUser.filter(t => t.id !== data.id);
        }
        data.createdOn = new Date(data.createdOn);
        this.teamNonUser.push(data);
        this.teamNonUser.sort((a, b) => (a.firstName + ' ' + a.lastName).localeCompare(b.firstName + ' ' + b.lastName));
        this.messageHandlerService.displaySuccess(this.isNewNonUserInTeam ?
          'USER-TEAM-ENROLLED-SUCCESFULLY' : 'USER-TEAM-UPDATED-SUCCESFULLY');
      },
      error: (error) =>
      {
        this.messageHandlerService.displayError(error);
        this.logger.error(error);
      }
    });
  }

  public onDeleteTeamNonUser(teamNonUser: TeamEdit): void
  {
    this.teamService.deleteCareTeam(Number(teamNonUser.id)).subscribe({
      next: () =>
      {
        this.teamNonUser = this.teamNonUser.filter(t => t.id !== teamNonUser.id);
        this.messageHandlerService.displaySuccess('USER-TEAM-REMOVED-SUCCESFULLY');
      },
      error: (error) =>
      {
        this.messageHandlerService.displayError(error);
        this.logger.error(error);
      }
    });
  }

  /**
   * Send a new message to the selected assignee.
   *
   * @param assignee Selected assignee.
   */
  public sendMessageToAssignee(assignee: string, isDirectAddress = false): void
  {
    const messageData: any = {};
    messageData.sendTo = assignee;
    messageData.isDirectAddress = isDirectAddress;
    this.messengerService.sendMessage(new MessageSubject('openComposeMessageDialog', messageData));
  }

  /**
   * Add this provider to the care team has been confirmed.
   */
  public addToCareTeam(): void
  {
    this.confirmAddToCareTeamDialogOpened = false;
    this.isNewNonUserInTeam = false;
    setTimeout(() => { this.editDataItemTeamNonUser = this.addingCareTeam; });
  }

  public getAutoCreatedHintText(formattedDate: string, cancelledReferral: boolean): string
  {
    return cancelledReferral ? this.cancelledReferralText : this.autoCreatedStaticText.replace('{{value}}', formattedDate);
  }


  public openAddAppointmentDialog(careTeam: Team): void
  {
    this.editDataItemAppointment = {
      provider: careTeam
    };
    this.messengerService.sendMessage(new MessageSubject('openAppointmentEditDialog',
      { editDataItemAppointment: this.editDataItemAppointment, memberId: this.memberId }));
  }

  public updateTeamMemberCollapsed(careTeamId: number, hide: boolean): void
  {
    this.collapseTeamMember.set(careTeamId, hide);
  }

  public isCollapsible(careTeam: any): boolean
  {
    return careTeam.homePhoneNumber || careTeam.cellPhoneNumber ||
      careTeam.faxNumber || careTeam.otherPhoneNumber || careTeam.workPhoneNumber;
  }

  public careTeamToggle(): void
  {
    if (this.caseCareTeamCollapsible !== undefined)
    {
      this.caseCareTeamCollapsible.toggleChange();
    }
    if (this.patientCareTeamCollapsible !== undefined)
    {
      this.patientCareTeamCollapsible.toggleChange();
    }
  }

  public showTooltip(e: ElementRef, tooltip: ElementRef): void
  {
    const tooltipDirective = this.tooltipDir.toArray().find(t => t.tooltipWrapper.nativeElement === tooltip);
    if (tooltipDirective)
    {
      tooltipDirective.toggle(e, true); // Show the tooltip
    }
  }
  
  public hideTooltip(tooltip: ElementRef): void
  {
    const tooltipDirective = this.tooltipDir.toArray().find(t => t.tooltipWrapper.nativeElement === tooltip);
    if (tooltipDirective)
    {
      tooltipDirective.hide(); // Show the tooltip
    }
  }
}
